
import { Lang } from "@cervval.js/common/src/i18n/languages";

export const INIT_REFRESH_REASON = "init";

// static properties added to constructor prototype
export const CUSTOM_ELEMENT_CLASS_CONFIG = "customElementClassConfig";

/** function that is the resolve function related to the waitForAfterInit Promise, it is called by CustomElement decorator after calling onAfterInit */
export const WAIT_FOR_AFTER_INIT_RESOLVER = "_waitForAfterInitResolver";

/**
 * Configuration that can be passed to CustomElement decorator
 */
export interface CustomElementConfig {

    /**
     * Name for the new custom element. Note that custom element names must contain a hyphen (ex component-tag).
     */
    name: string;

    /**
     * Options passed to window.customElements.define
     */
    definitionOptions?: ElementDefinitionOptions;

    /**
     * Html template
     * If none provided and the decorated component inherits from a CustomElement, the parent's template will be used
     */
    template?: string;

    /**
     * CSS to attach to component ({ default } is needed when webpack is loading modules)
     * If the decorated component inherits from a CustomElement, its parent style will be automatically attached and there is no need to include it again
     */
    style?: string | { default: string };

    /**
     * CSS classes to add in element div (ex: <my-element class="classToAdd"></my-element>) 
     * * If used with attachShadow note that the shadow dom won't let you access the component's classes and you can't rely on these classes in the component css
     */
    elementCssClasses?: string[];

    attachShadow?: ShadowRootInit;// By default template and style are attached to the light DOM, set this option to attach component to shadow dom

    /**
     * This binds slot elements when using light DOM (ie when attachShadow is undefined or null) as if we were using a shadow DOM
     * ! This is experimental
     */
    bindChildrenSlots?: boolean;

    /** 
     * I18n definition of component
     * can be :
     * - a single function (lang?: Lang) => object 
     * - an array of functions ((lang?: Lang) => object)[]
     * - an object {"text" : "text"}
     * 
     * If the decorated component inherits from a CustomElement, its parent i18n will be merged with this component i18n and there is no need to include it again
     * But you should set both I18n classes in the WebComponent class definition (ex: class ImagePicker extends FilePicker<I18nFilePicker & I18nImagePicker>)
     */
    i18n?: ((lang?: Lang) => object) | ((lang?: Lang) => object)[] | object;

    /** if true, refresh will not be called after init */
    doNotRefreshAfterInit?: boolean;
}

/**
 * This configuration can be passed to the WebComponent constructor in order to bypass the @CustomElement decorator configuration for one instance
 */
export interface OverrideCustomElementConfig {

    /**
     * Set to false to disable the attachShadow defined by the decorated
     */
    attachShadow?: ShadowRootInit | false;

    /**
     * Set true enable binding to webComponent children slots when using light DOM (binding is done only once on connectedCallback)
     */
    bindChildrenSlots?: boolean;

    /**
     * CSS classes to add in element div (ex: <my-element class="classToAdd"></my-element>) 
     */
    elementCssClasses?: string[];

    /**
     * If true refresh will not be called
     * 
     * By default, if you leave doNotRefreshAfterInit undefined in a decorated PageComponent,
     * the PageComponent will set doNotRefreshAfterInit to true to prevent having refresh("init") before refresh("pageShow")
     */
    doNotRefreshAfterInit?: boolean;
}

/**
 * Configuration that is used by WebComponent constructor to build the component
 */
export interface CustomElementClassConfig {

    /**
     * Parent config (Super class that is also decorated with @CustomElement)
     */
    parentConfig?: CustomElementClassConfig;

    /**
     * Class name of the WebComponent
     */
    className?: string;

    /**
     * Config defined on class decorator
     */
    config: CustomElementConfig;

    /**
     * original refresh function
     */
    original_refresh?: () => void;

    /**
     * The style to append to the DOM 
     * * this is not exactly the same as config.style since we prepare the style before adding it to the DOM
     */
    style?: string;

    /**
     * The html template to clone for each instance
     */
    template?: HTMLTemplateElement;
}

export interface WebComponentConnectionListener {
    /** 
     * optional element that installed the listener, 
     * if origin is set and addConnectionListener is called twice with this origin, the second addConnectionListener will be ignored
     */
    origin?: unknown;
    /** called after component.disconnectedCallback */
    disconnected?: () => void;
    /** called after component.connectedCallback */
    connected?: () => void;
}

/**
 * Constructor function
 */
export interface ConstructorFunction<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new(...args: any[]): T;
}
